import { useRef, useState, useEffect, useMemo } from "react";
import { PairInfo } from "@/types";
import { getPairColor } from "@trader-dashboard/shared";

const CELL_SIZE = 14;
const GAP = 3;
const ROWS = 2;

type OpenOrdersGridProps = {
  pairs: PairInfo[];
  totalOpenOrders: number;
  label?: React.ReactNode;
};

export const OpenOrdersGrid = ({ pairs, totalOpenOrders, label }: OpenOrdersGridProps): React.ReactNode => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [cols, setCols] = useState(16);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      // cols * CELL_SIZE + (cols - 1) * GAP <= width
      // cols * (CELL_SIZE + GAP) <= width + GAP
      const fitCols = Math.floor((width + GAP) / (CELL_SIZE + GAP));
      setCols(Math.max(fitCols, 1));
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const totalCells = cols * ROWS;

  const cells = useMemo(() => {
    if (totalOpenOrders === 0 || pairs.length === 0) return [];

    const activePairs = pairs
      .filter(p => (p.openOrders ?? 0) > 0)
      .sort((a, b) => (b.openOrders ?? 0) - (a.openOrders ?? 0));

    if (activePairs.length === 0) return [];

    const result: { color: string; symbol: string }[] = [];

    if (totalOpenOrders <= totalCells) {
      for (const pair of activePairs) {
        const count = pair.openOrders ?? 0;
        const color = getPairColor(pair.symbol);
        const symbol = pair.symbol.replace('.PRO', '');
        for (let i = 0; i < count; i++) {
          result.push({ color, symbol });
        }
      }
      const emptyCells = totalCells - result.length;
      for (let i = 0; i < emptyCells; i++) {
        result.push({ color: '', symbol: '' });
      }
    } else {
      const allocations = activePairs.map(pair => {
        const count = pair.openOrders ?? 0;
        const exact = (count / totalOpenOrders) * totalCells;
        return {
          symbol: pair.symbol.replace('.PRO', ''),
          color: getPairColor(pair.symbol),
          floor: Math.floor(exact),
          remainder: exact - Math.floor(exact),
        };
      });

      let allocated = allocations.reduce((sum, a) => sum + a.floor, 0);
      const remaining = totalCells - allocated;

      const sorted = allocations
        .map((a, idx) => ({ idx, remainder: a.remainder }))
        .sort((a, b) => b.remainder - a.remainder);

      for (let i = 0; i < remaining; i++) {
        allocations[sorted[i].idx].floor += 1;
      }

      for (const alloc of allocations) {
        if (alloc.floor < 1) alloc.floor = 1;
      }

      for (const alloc of allocations) {
        for (let i = 0; i < alloc.floor; i++) {
          result.push({ color: alloc.color, symbol: alloc.symbol });
        }
      }

      while (result.length > totalCells) {
        result.pop();
      }
    }

    return result;
  }, [pairs, totalOpenOrders, totalCells]);

  if (totalOpenOrders === 0 || pairs.length === 0) {
    return <div ref={containerRef} />;
  }

  const gridWidth = cols * CELL_SIZE + (cols - 1) * GAP;

  return (
    <div ref={containerRef}>
      {label && <div style={{ width: gridWidth }}>{label}</div>}
      <div
        className="grid"
        style={{
          gridTemplateColumns: `repeat(${cols}, ${CELL_SIZE}px)`,
          gap: `${GAP}px`,
        }}
      >
      {cells.map((cell, i) => (
        <div
          key={i}
          className="rounded-[2px] border border-gray-200"
          style={{
            width: CELL_SIZE,
            height: CELL_SIZE,
            backgroundColor: cell.color || '#ffffff',
          }}
          title={cell.symbol || undefined}
        />
      ))}
      </div>
    </div>
  );
};
